/******/ (() => { // webpackBootstrap
class CollapsibleButton extends HTMLElement {
  constructor() {
    super();
    this.THROTTLE_INTERVAL = 100;
    this.bottomGapHeight = 15;
    this.contentBlockheight = () => this.nextElementSibling.querySelector('.content').offsetHeight + this.bottomGapHeight + 'px';
  }

  connectedCallback() {
    this.init();
  }

  init() {
    window.wetheme.webcomponentRegistry.register({key: 'collapsible-button'});
    this.collapsibleContent = this.parentElement.querySelector('.collapsible-content');
    if(this.nextElementSibling.classList.contains('collapsible-content') && this.parentElement.classList.contains('collapsible-tab')) {

      // Set initial height based on state
      this.parentElement.classList.contains('open') ? this.nextElementSibling.style.height = 'fit-content' : this.nextElementSibling.style.height = '0px';

      // GUILD LANE — accessibility. Envy shipped this element with `tabindex="0"` and an
      // Enter handler and nothing else: no role, no aria-expanded, no aria-controls. It is
      // therefore focusable but announces as plain text, and a screen reader cannot tell an
      // open question from a closed one. Measured 2026-08-16 on /pages/gild-faqs: 29 triggers,
      // 0 with aria-expanded — the only 5 on the page belong to the header's nav, cart drawer
      // and search. That is all 135 FAQ answers unreachable to assistive tech.
      //
      // Fixed here rather than in the six calling sections so every collapsible benefits.
      // Space is added alongside Enter because role="button" promises it; declaring the role
      // without honouring the key would be worse than leaving the role off.
      this.setupAccessibility();

      // Handle click
      this.addEventListener('click', this.toggle);

      // Handle enter and space keys
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          // Space scrolls the page by default, which fires while the panel is opening.
          e.preventDefault();
          this.toggle();
        }
      });

      // Handle window resize
      window.addEventListener('resize', this.handleResize.bind(this));

      // Handle collapsible tab content size change
      this.nextElementSibling.addEventListener('click', this.resetHeight.bind(this));

      // Handle Shopify theme editor events
      document.addEventListener('shopify:block:select', (event) => {
        // If it's open by default, return
        if (this.parentElement.dataset.openByDefault === 'true') return;
        // If the selected block is the same as this block, show details
        this.blockId = this.dataset.blockId;
        if (this.blockId && event.detail.blockId === this.blockId) {
          this.showDetails();
        }
      });

      document.addEventListener('shopify:block:deselect', () => {
        // If it's open by default, return
        if (this.parentElement.dataset.openByDefault === 'true') return;
        // If the selected block is the same as this block, hide details
        this.hideDetails();
      });
    }
  }

  // GUILD LANE — see the note in init(). Gives the trigger the disclosure semantics Envy
  // omitted, without changing any markup in the calling sections.
  setupAccessibility() {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    // aria-controls needs the panel to be addressable. Most callers do not give it an id,
    // so mint a stable one from the block id where there is one, and fall back to a counter.
    const panel = this.nextElementSibling;
    if (panel && !panel.id) {
      CollapsibleButton.panelCount = (CollapsibleButton.panelCount || 0) + 1;
      const suffix = this.dataset.blockId || CollapsibleButton.panelCount;
      panel.id = `collapsible-panel-${suffix}-${CollapsibleButton.panelCount}`;
    }
    if (panel) this.setAttribute('aria-controls', panel.id);

    this.syncExpandedState();
  }

  syncExpandedState() {
    this.setAttribute('aria-expanded', this.parentElement.classList.contains('open') ? 'true' : 'false');
  }

  handleResize() {
    this.throttle(this.resetHeight, this.THROTTLE_INTERVAL);
  }

  resetHeight() {
    if(!this.parentElement.classList.contains('open')) return;
    this.setTransitionHeight(this.nextElementSibling, false);
  }

  toggle() {
    this.parentElement.classList.contains('open') ? this.hideDetails() : this.showDetails();
  }

  showDetails() {
    this.setTransitionHeight(this.nextElementSibling, false)
    this.parentElement.classList.add('open');
    this.syncExpandedState(); // GUILD LANE

    if (this.collapsibleContent) {
      this.collapsibleContent.addEventListener('transitionend', this.transitionEndHandler.bind(this), {once: true});
    }
  }

  hideDetails() {
    this.setTransitionHeight(this.nextElementSibling, true)
    this.parentElement.classList.remove('open');
    this.syncExpandedState(); // GUILD LANE

    if (this.collapsibleContent) {
      this.collapsibleContent.style.overflow = 'hidden';
    }
  }

  setTransitionHeight(container, isOpen) {
    if(isOpen) {
      container.style.height = '0px';
    } else {
      container.style.height = this.contentBlockheight();
    }
  }

  transitionEndHandler(event) {
    // Check if the transitionend event is for the height property
    if (event.propertyName === 'height') {
      this.collapsibleContent.style.overflow = 'visible';
    }
  }

  throttle (callback, time) {
    if (this.throttleTimer) return;
    this.throttleTimer = true;
    setTimeout(() => {
      callback.call(this);
      this.throttleTimer = false;
    }, time);
  }
}

// Only define the custom element if it doesn't already exist
if (!customElements.get('collapsible-button')) customElements.define('collapsible-button', CollapsibleButton);

/******/ })()
;